import React, { useState, useEffect, useRef, useCallback } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";

import { Stack } from "@mui/joy";

import { FileApi } from "../api/file";
import { ApiFile, ApiFragmentNode, ApiMessageTypes } from "../shared/interfaces";
import { PrimeQueryKeys } from "../shared/constants";

import FileViewer, { FileViewerArgs } from "./fileviewer/FileViewer";
import FloatingRelatedItems from "./nodes/FloatingRelatedItems";
import useCreateConversationAndNavigate from "../hooks/useCreateConversationAndNavigate";
import { setPageTitle } from "../util/elementUtil";

const FileViewerMemo = React.memo(FileViewer);

const styles = {
    topContainer: { overflow: "hidden", flexGrow: 1 },
    fileViewerContainer: { width: "calc(100vw)" },
};

export default function Document() {
    const navigate = useNavigate();
    const createConversation = useCreateConversationAndNavigate();

    const { id = "", fragmentNodeId } = useParams();

    const [selectedFragmentNode, setSelectedFragmentNode] = useState<ApiFragmentNode | null>(null);
    const hasFetchedNodeFromUrl = useRef(false);

    // Mark as scrolling if we have a target fragment node
    const [shouldScrollToSelectedFragmentNode, setShouldScrollToSelectedFragmentNode] =
        useState(!!fragmentNodeId);

    // Update selected fragment node and URL
    const handleSetSelectedFragmentNode = useCallback(
        (node: ApiFragmentNode | null, shouldScroll: boolean = false) => {
            setSelectedFragmentNode(node);
            setShouldScrollToSelectedFragmentNode(shouldScroll);
            navigate(`/doc/${id}${node ? `/${node.id}` : ""}`);
        },
        [navigate, id],
    );

    // Basic file metadata
    const fileQuery = useQuery<ApiFile, any>(PrimeQueryKeys.file(id), () => FileApi.getFile(id), {
        retry: (_, error) => error.response?.status !== 404,
    });

    // Update selected fragment node based on URL
    const urlFragmentNodeQuery = useQuery<ApiFragmentNode>(
        PrimeQueryKeys.fragmentNode(fragmentNodeId ?? ""),
        () => FileApi.getFragmentNode(fragmentNodeId!),
        {
            enabled: !!fragmentNodeId,
        },
    );

    useEffect(() => {
        if (!hasFetchedNodeFromUrl.current && urlFragmentNodeQuery.data) {
            setSelectedFragmentNode(urlFragmentNodeQuery.data);
            hasFetchedNodeFromUrl.current = true;
        }
    }, [urlFragmentNodeQuery.data]);

    useEffect(() => {
        const baseTitle = selectedFragmentNode
            ? selectedFragmentNode.title
            : fileQuery.data?.fileName;
        setPageTitle(baseTitle);
    }, [fileQuery.data, selectedFragmentNode]);

    const allFilesQuery = useQuery(PrimeQueryKeys.allFiles, FileApi.getAllFiles);
    const files = allFilesQuery.data ?? [];

    // If no id user clicked the documents tab
    if (id === "" && files.length > 0) {
        return <Navigate to={`/doc/${files[0].id}`} />;
    }

    if (fileQuery.isError) {
        const status = (fileQuery.error as any)?.response?.status;
        return status === 404 ? (
            <p>File not found. Please check the URL.</p>
        ) : (
            <p>Something went wrong. Please try again later.</p>
        );
    }

    const fileArgs: FileViewerArgs = {
        file: fileQuery.data!,
        scrollToFragmentNode: shouldScrollToSelectedFragmentNode ? selectedFragmentNode : null,
        selectedFragmentNodeId: selectedFragmentNode ? selectedFragmentNode.id : null,
    };

    return (
        <Stack sx={styles.topContainer} direction="row">
            {selectedFragmentNode && (
                <FloatingRelatedItems
                    fragmentNode={selectedFragmentNode}
                    onClose={() => handleSetSelectedFragmentNode(null)}
                    setFragmentNode={(selectedFragmentNode) =>
                        handleSetSelectedFragmentNode(selectedFragmentNode, true)
                    }
                    onMessageSent={(message, node) => {
                        createConversation(message, ApiMessageTypes.Markdown, node);
                    }}
                />
            )}

            {fileArgs.file && (
                <Stack sx={styles.fileViewerContainer}>
                    <FileViewerMemo
                        fileArgs={fileArgs}
                        showTitleAndClose={false}
                        showSidebar={true}
                        onFragmentNodeSelected={(node) => {
                            handleSetSelectedFragmentNode(node, false);
                        }}
                    />
                </Stack>
            )}
        </Stack>
    );
}
